import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Avatar,
  AvatarGroup,
  IconButton,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Visibility as VisibilityIcon,
  Description as DescriptionIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import KanbanBoard from '../../common/KanbanBoard';
import {
  TASK_STATUS_COLUMNS,
  resolveTaskStatus,
  getTaskStatusMeta,
} from '../../../utils/taskStatus';
import { canEditTask, canUpdateTaskStatus, canArchiveTask } from '../../../utils/taskPermissions';

const MiniProgress = ({ value }) => {
  const v = Number.isFinite(Number(value)) ? Number(value) : 0;
  const color = v >= 80 ? '#10b981' : v >= 40 ? '#f59e0b' : '#ef4444';
  return (
    <Box>
      <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.65rem', color, fontWeight: 700 }}>
        {v}%
      </Typography>
      <Box sx={{ height: 4, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.08)', mt: 0.4, overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: `${Math.min(v, 100)}%`, bgcolor: color, borderRadius: 4 }} />
      </Box>
    </Box>
  );
};

const B = {
  500: '#3B82F6',
  700: '#1D4ED8',
};

const KanbanCard = ({
  task,
  user,
  onOpen,
  onEdit,
  onArchive,
  draggable,
  isDragging,
}) => {
  const theme = useTheme();
  const statusMeta = getTaskStatusMeta(resolveTaskStatus(task));

  return (
    <Box
      component={motion.div}
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      sx={{
        p: 1.5,
        mb: 1.25,
        borderRadius: '12px',
        cursor: draggable ? 'grab' : 'pointer',
        background:
          'linear-gradient(160deg, rgba(15,23,42,0.95) 0%, rgba(30,41,59,0.9) 100%)',
        border: `1px solid ${alpha(statusMeta.color, 0.25)}`,
        boxShadow: isDragging
          ? `0 12px 32px ${alpha(statusMeta.color, 0.35)}`
          : `0 4px 16px rgba(0,0,0,0.25)`,
        opacity: isDragging ? 0.85 : 1,
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
      
        '&:hover': {
          borderColor: alpha(statusMeta.color, 0.5),
          boxShadow: `0 8px 24px ${alpha(statusMeta.color, 0.2)}`,
        },
      
        '&:active': draggable
          ? { cursor: 'grabbing' }
          : undefined,
      }}
      onClick={() => onOpen(task)}
      >
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: '0.9rem',
          color: '#fff',
          mb: 0.75,
          lineHeight: 1.3,
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        {task.title}
      </Typography>
      {task.description ? (
        <Typography
          sx={{
            fontSize: '0.75rem',
            color: 'rgba(255,255,255,0.55)',
            mb: 1,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {task.description}
        </Typography>
      ) : null}

      <Box sx={{ mb: 1 }}>
        <MiniProgress value={task.progress ?? 0} />
      </Box>

      {task.departments?.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
          {task.departments.slice(0, 2).map((dept) => (
            <Chip
              key={dept.id}
              label={dept.name}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.6rem',
                bgcolor: 'rgba(219,234,254,0.15)',
                color: B[500],
                border: '1px solid rgba(147,197,253,0.3)',
              }}
            />
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 26, height: 26, fontSize: '0.7rem' } }}>
          {task.users?.map((u) => (
            <Tooltip key={u.id} title={u.name} arrow>
              <Avatar
                sx={{
                  background: `linear-gradient(135deg, ${B[500]}, ${B[700]})`,
                  fontSize: '0.7rem',
                }}
              >
                {(u.name || '?').charAt(0).toUpperCase()}
              </Avatar>
            </Tooltip>
          ))}
        </AvatarGroup>

        <Box sx={{ display: 'flex', gap: 0.25 }} onClick={(e) => e.stopPropagation()}>
          <Tooltip title="View" arrow>
            <IconButton size="small" onClick={() => onOpen(task)} sx={{ color: theme.palette.info.main }}>
              <VisibilityIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          {canEditTask(user, task) && (
            <Tooltip title="Edit" arrow>
              <IconButton size="small" onClick={() => onEdit(task)} sx={{ color: '#f59e0b' }}>
                <EditIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          {canArchiveTask(user, task) && onArchive && (
            <Tooltip title="Archive task" arrow>
              <IconButton size="small" onClick={() => onArchive(task)} sx={{ color: '#ef4444' }}>
                <ArchiveIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          {task.documentation_link && (
            <Tooltip title="Documentation" arrow>
              <IconButton
                size="small"
                component="a"
                href={task.documentation_link}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: '#10b981' }}
              >
                <DescriptionIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          {task.project_link && (
            <Tooltip title="Project link" arrow>
              <IconButton
                size="small"
                component="a"
                href={task.project_link}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: '#10b981' }}
              >
                <LinkIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const TaskKanban = ({ tasks, user, onStatusChange, onOpenTask, onEditTask, onArchiveTask, updatingTaskId }) => (
  <KanbanBoard
    columns={TASK_STATUS_COLUMNS}
    items={tasks}
    getItemId={(t) => t.id}
    resolveColumnId={resolveTaskStatus}
    canDragItem={(t) => canUpdateTaskStatus(user, t)}
    onColumnChange={onStatusChange}
    updatingItemId={updatingTaskId}
    dragMimeType="application/x-task-id"
    emptyMessage="Drop tasks here"
    renderCard={(task, { isDragging, draggable }) => (
      <KanbanCard
        task={task}
        user={user}
        onOpen={onOpenTask}
        onEdit={onEditTask}
        onArchive={onArchiveTask}
        draggable={draggable}
        isDragging={isDragging}
      />
    )}
  />
);

export default TaskKanban;
